import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import {
  Province,
  ProvincesDataPagingRes,
} from '@/shared/interfaces/erp/system-settings';
import { getUrlParamsFetch, handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';
import { NavigateFunction } from 'react-router-dom';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchProvinces = (
  params?: getProvincesParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['provinces', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getProvinces(params);
    },
    enabled,
  });
};

export const useGetProvince = (id: string) => {
  return useQuery({
    queryKey: ['provinces', id],
    queryFn: async () => {
      return getProvince(id);
    },
  });
};

export const useCreateProvince = ({
  navigate,
   
  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createProvince,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provinces'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Provincia creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateProvince = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateProvince,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provinces'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Usuario creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveProvince = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeProvince,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provinces'] });
      navigate(returnUrl);
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

export const useGetProvinceByCountry = ({ pais = '', page_size = 10 }) => {
  return useQuery({
    queryKey: ['province'],
    queryFn: async () => {
      return getProvinceByCountry(pais, page_size);
    },
  });
};

///* axios
export type getProvincesParams = Partial<Province> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
  pais_codigo?: string;
};

export type CreateProvinceParams = Omit<Province, 'id_provincia'>;

export type UpdateProvinceParams = {
  id: string;
  data: CreateProvinceParams;
};

export const getProvinces = async (params?: getProvincesParams) => {

  const queryParams = getUrlParamsFetch(params || {});

  return get<ProvincesDataPagingRes>(
    `${urlBackend}api/v1/provincia/?${queryParams}`,
    true
  );
};

export const getProvince = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<Province>(`${urlBackend}api/v1/provincia/${id}`, true);

  res.status === 'error' && setAlert(true, res.message, 'error');
  return res;
};

export const createProvince = async (data: CreateProvinceParams) => {
  const setAlert = useUiStore.getState().setAlert;
  
  const res = await post<Province>(
    `${urlBackend}api/v1/provincia/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateProvince = async ({ id, data }: UpdateProvinceParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Province>(
    `${urlBackend}api/v1/provincia/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const removeProvince = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Province>(
    `${urlBackend}api/v1/provincia/${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const getProvinceByCountry = async (pais: string, page_size: number) => {
  const res = await get<ProvincesDataPagingRes>(
    `${urlBackend}api/v1/provincia/?pais=${pais}&page_size=${page_size}`,
    true
  );
  return res;
};
